import {audioFields,isAudioAsset} from "./playlist-audio.js";
import {staticOverlayFields} from "./static-overlay-collection.js";
import { validateOverlay } from "./hotspot-overlay.js";
import { trustedArtifactUrl, artifactUploadHeaders } from "./content-url.js";
export const Orientation = Object.freeze({ portrait: 1, landscape: 2 });
export const PlaybackMode = Object.freeze({ static: 1, horizontal: 2, vertical: 3 });
export const Direction = Object.freeze({ left: 1, right: 2, up: 3, down: 4 });
export const DeviceProfile = Object.freeze({
  uno: "PIRDY_C3_28_NOTOUCH",
  unoTouch: "PIRDY_C3_28_TOUCH",
  duos: "PIRDY_C3_35_DUOS_NOTOUCH",
  duosTouch: "PIRDY_C3_35_DUOS_TOUCH",
  cyd28Touch: "SCROLLER_CYD_28_TOUCH",
  cyd28St7789Touch: "SCROLLER_CYD_28_ST7789_TOUCH",
  cyd28St7789Secure: "SCROLLER_CYD_28_ST7789_SECURE",
  cyd35Touch: "SCROLLER_CYD_35_TOUCH",
  crowPanel50Touch: "PIRDY_CROWPANEL_50_TOUCH",
  crowPanel70Touch: "PIRDY_CROWPANEL_70_TOUCH",
});

const HEADER_SIZE = 36;
const MAX_HORIZONTAL_WIDTH = 16_384;
const MAX_VERTICAL_HEIGHT = 4_096;

export function requireDeviceProfileId(profileId) {
  const canonical = typeof profileId === "string" ? profileId.trim().toUpperCase() : "";
  if (!Object.values(DeviceProfile).includes(canonical))
    throw new Error("The Pirdy display profile is missing or unknown. Select a supported Pirdy model.");
  return canonical;
}

// The omitted argument retains the original Uno-only authoring API contract.
// Explicit publication targets must be validated, never silently defaulted.
export function viewportFor(orientation, profileId = DeviceProfile.uno) {
  const canonical = requireDeviceProfileId(profileId);
  const duos = canonical === DeviceProfile.duos || canonical === DeviceProfile.duosTouch || canonical === DeviceProfile.cyd35Touch;
  const native = [DeviceProfile.crowPanel50Touch, DeviceProfile.crowPanel70Touch].includes(canonical) ? { width: 800, height: 480 }
    : duos ? { width: 480, height: 320 } : { width: 320, height: 240 };
  return Number(orientation) === Orientation.portrait
    ? { width: native.height, height: native.width }
    : native;
}

// Geometry-only recovery for historical prepared assets, NOT device identity or
// Touch capability inference. Current assets carry the exact target profile.
export function geometryProfileIdForAsset(asset) {
  const declared = [asset?.targetProfileId, asset?.profileId]
    .find(value => value != null && value !== "");
  if (declared !== undefined) return requireDeviceProfileId(declared);
  const orientation = Number(asset?.orientation ?? asset?.compatibleOrientation ?? Orientation.landscape);
  const width = Number(asset?.width), height = Number(asset?.height);
  const declaredMode = asset?.playbackMode == null ? null : Number(asset.playbackMode);
  if (![Orientation.portrait, Orientation.landscape].includes(orientation) ||
      !Number.isSafeInteger(width) || !Number.isSafeInteger(height) || width <= 0 || height <= 0)
    throw new Error("The legacy artwork needs valid geometry and a supported Pirdy target profile.");
  const candidates = [DeviceProfile.uno, DeviceProfile.duos, DeviceProfile.crowPanel70Touch].filter(profile => {
    const viewport = viewportFor(orientation, profile);
    const mode = width === viewport.width && height === viewport.height ? PlaybackMode.static
      : orientation === Orientation.landscape && height === viewport.height && width > viewport.width && width <= MAX_HORIZONTAL_WIDTH ? PlaybackMode.horizontal
      : width === viewport.width && height > viewport.height && height <= MAX_VERTICAL_HEIGHT ? PlaybackMode.vertical
      : null;
    return mode !== null && (declaredMode === null || declaredMode === mode || (declaredMode === 4 && mode === PlaybackMode.static));
  });
  // This includes positively identified historical Uno library representatives;
  // arbitrary geometry must not silently become an Uno target.
  if (candidates.length === 1) return candidates[0];
  throw new Error("The legacy artwork needs compatible geometry and a supported Pirdy target profile.");
}

export async function prepareArtwork(file, presentation, orientation, background = "#000", profileId = DeviceProfile.uno) {
  if (!(file instanceof Blob)) throw new Error("Choose a PNG or JPEG image.");
  if (!["image/png", "image/jpeg"].includes(file.type)) throw new Error("Pirdy accepts PNG or JPEG artwork.");
  const image = await createImageBitmap(file);
  try {
    return await prepareDecodedArtwork(image, file.name, presentation, orientation, background, profileId);
  } finally {
    image.close();
  }
}

async function prepareDecodedArtwork(image, fileName, presentation, orientation, background, profileId) {
    const plan = preparationPlan(image.width, image.height, presentation, orientation, profileId);
    const canvas = document.createElement("canvas");
    canvas.width = plan.outputWidth;
    canvas.height = plan.outputHeight;
    const context = canvas.getContext("2d", { alpha: false, willReadFrequently: true });
    if (!context) throw new Error("This browser cannot prepare Pirdy artwork.");
    context.fillStyle = background;
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.drawImage(image, plan.destinationX, plan.destinationY, plan.scaledWidth, plan.scaledHeight);
    const rgba = context.getImageData(0, 0, canvas.width, canvas.height).data;
    const encoded = encodePimg(canvas.width, canvas.height, rgba, plan.playbackMode === PlaybackMode.horizontal);
    const sha256 = bytesToHex(await crypto.subtle.digest("SHA-256", encoded.bytes));
    return {
      blob: new Blob([encoded.bytes], { type: "application/octet-stream" }),
      previewUrl: canvas.toDataURL("image/png"),
      metadata: {
        name: String(fileName ?? "").replace(/\.[^.]+$/, "") || "Pirdy artwork",
        sha256,
        fileSize: encoded.bytes.byteLength,
        width: canvas.width,
        height: canvas.height,
        orientation: Number(orientation),
        playbackMode: plan.playbackMode,
        pixelFormat: 1,
        crc32: encoded.crc32,
        extension: ".pimg",
      },
    };
}

export async function inspectContentSource(file) {
  if (!(file instanceof Blob)) throw new Error("Choose a PNG or JPEG image.");
  if (!["image/png", "image/jpeg"].includes(file.type)) throw new Error("Pirdy accepts PNG or JPEG artwork.");
  if (file.size < 1 || file.size > 20 * 1024 * 1024) throw new Error("The source image must be between 1 byte and 20 MB.");
  const image = await createImageBitmap(file);
  try {
    if (image.width <= 0 || image.height <= 0 || image.width * image.height > 16_000_000)
      throw new Error("The source image must be no more than 16 megapixels.");
    return {
      blob: file,
      metadata: {
        name: contentSourceName(file.name),
        sourceFileName: file.name || "artwork",
        sourceContentType: file.type,
        sha256: bytesToHex(await crypto.subtle.digest("SHA-256", await file.arrayBuffer())),
        fileSize: file.size,
        width: image.width,
        height: image.height,
      },
    };
  } finally {
    image.close();
  }
}

export function contentSourceName(fileName) {
  return String(fileName ?? "").replace(/\.[^.]+$/, "") || "Pirdy artwork";
}

export async function prepareBranding(file) {
  if (!(file instanceof Blob) || !["image/png", "image/jpeg"].includes(file.type))
    throw Object.assign(new Error("Choose a PNG or JPEG startup logo."), { customerSafe: true });
  if (file.size < 1 || file.size > 20 * 1024 * 1024)
    throw Object.assign(new Error("Choose a nonempty PNG or JPEG smaller than 20 MB."), { customerSafe: true });
  // Share one decode across the four small output canvases. A highly compressed
  // logo can be much larger than ordinary artwork; four concurrent full decodes
  // previously multiplied its memory use without changing the output quality.
  const image = await createImageBitmap(file);
  try {
    const [landscape, portrait, duosLandscape, duosPortrait, crowLandscape, crowPortrait] = await Promise.all([
      prepareDecodedArtwork(image, file.name, "fit", Orientation.landscape, "#fff", DeviceProfile.uno),
      prepareDecodedArtwork(image, file.name, "fit", Orientation.portrait, "#fff", DeviceProfile.uno),
      prepareDecodedArtwork(image, file.name, "fit", Orientation.landscape, "#fff", DeviceProfile.duos),
      prepareDecodedArtwork(image, file.name, "fit", Orientation.portrait, "#fff", DeviceProfile.duos),
      prepareDecodedArtwork(image, file.name, "fit", Orientation.landscape, "#fff", DeviceProfile.crowPanel70Touch),
      prepareDecodedArtwork(image, file.name, "fit", Orientation.portrait, "#fff", DeviceProfile.crowPanel70Touch),
    ]);
    return { landscape, portrait, duosLandscape, duosPortrait, crowLandscape, crowPortrait };
  } finally { image.close(); }
}

export async function uploadBlob(uploadUrl, blob) {
  if (!uploadUrl) return;
  const url = trustedContentUrl(uploadUrl, "Pirdy refused an untrusted upload destination.");
  let response;
  try {
    response = await fetch(url, {
      method: "PUT",
      headers: artifactUploadHeaders(url),
      body: blob,
    });
  } catch {
    throw new Error("Artwork could not be uploaded. Check your connection and try again; existing content was not changed.");
  }
  if (!response.ok)
    throw new Error("Artwork could not be uploaded. Refresh your account data and try again; existing content was not changed.");
}

export async function previewPimg(downloadUrl, maximumExtent = 520) {
  const url = trustedContentUrl(downloadUrl, "Pirdy refused an untrusted content source.");
  let response;
  try { response = await fetch(url, { cache: "no-store" }); }
  catch { throw new Error("Content preview could not be loaded. Check your connection, then try again."); }
  if (!response.ok) throw new Error("Content preview could not be loaded. Refresh your account data, then try again.");
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (bytes.length < HEADER_SIZE || String.fromCharCode(...bytes.subarray(0, 4)) !== "PIMG")
    throw new Error("The stored Pirdy image header is invalid.");
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const width = view.getUint32(8, true);
  const height = view.getUint32(12, true);
  const columnMajor = (view.getUint16(18, true) & 1) !== 0;
  const payloadLength = Number(view.getBigUint64(20, true));
  if (!width || !height || payloadLength !== width * height * 2 || bytes.length !== HEADER_SIZE + payloadLength)
    throw new Error("The stored Pirdy image dimensions are invalid.");
  if (crc32Of(bytes.subarray(HEADER_SIZE)) !== view.getUint32(28, true))
    throw new Error("The stored Pirdy image failed CRC validation.");
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) throw new Error("This browser cannot display Pirdy artwork.");
  const image = context.createImageData(width, height);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const targetPixel = y * width + x;
      const sourcePixel = columnMajor ? x * height + y : targetPixel;
      const value = view.getUint16(HEADER_SIZE + sourcePixel * 2, false);
      const target = targetPixel * 4;
      image.data[target] = ((value >> 11) & 0x1f) * 255 / 31;
      image.data[target + 1] = ((value >> 5) & 0x3f) * 255 / 63;
      image.data[target + 2] = (value & 0x1f) * 255 / 31;
      image.data[target + 3] = 255;
    }
  }
  context.putImageData(image, 0, 0);
  const scale = Math.min(1, maximumExtent / Math.max(width, height));
  if (scale === 1) return canvas.toDataURL("image/png");
  const thumbnail = document.createElement("canvas");
  thumbnail.width = Math.max(1, Math.round(width * scale));
  thumbnail.height = Math.max(1, Math.round(height * scale));
  const thumbnailContext = thumbnail.getContext("2d", { alpha: false });
  thumbnailContext.imageSmoothingEnabled = true;
  thumbnailContext.imageSmoothingQuality = "high";
  thumbnailContext.drawImage(canvas, 0, 0, thumbnail.width, thumbnail.height);
  return thumbnail.toDataURL("image/png");
}

function trustedContentUrl(value, message) {
  return trustedArtifactUrl(value, message);
}

export function createPlaylistItem(asset, values = {}) {
  if(isAudioAsset(asset))throw new Error("Attach audio in an artwork sound setting instead of adding it as an image.");
  const playbackMode = Number(asset.playbackMode);
  const requestedPresentation = Number(values.requestedPresentation ?? (playbackMode === PlaybackMode.static ? 1 : 3));
  const base = {
    assetId: asset.assetId,
    autoplay: values.autoplay !== false,
    transitionEffect: Number(values.transitionEffect ?? 1),
    transitionDurationMs: clampInteger(values.transitionDurationMs ?? 500, 0, 10_000),
    interactions: values.interactions?.touchAreas?.length ? values.interactions : null,
    ...staticOverlayFields(values), ...audioFields(values),
    requestedPresentation,
    requestedHorizontalDirection: Number(values.requestedHorizontalDirection ?? Direction.left),
    requestedVerticalDirection: Number(values.requestedVerticalDirection ?? Direction.up),
    requestedHorizontalSpeedPxPerSecond: Number(values.requestedHorizontalSpeedPxPerSecond ?? 10),
    requestedVerticalSpeedPxPerSecond: Number(values.requestedVerticalSpeedPxPerSecond ?? 5),
  };
  if (requestedPresentation === 1) {
    return {
      type: "static", ...base, effectIn: Number(values.effectIn ?? 1), effectOut: Number(values.effectOut ?? 1),
      effectDurationMs: clampInteger(values.effectDurationMs ?? 500, 0, 10_000),
      holdDurationMs: clampInteger(values.holdDurationMs ?? 5000, 100, 3_600_000), presentationMode: 1,
    };
  }
  const sourceWidth = Number(asset.sourceWidth ?? asset.width);
  const sourceHeight = Number(asset.sourceHeight ?? asset.height);
  const vertical = playbackMode === PlaybackMode.vertical ||
    (playbackMode === PlaybackMode.static && sourceWidth * Number(asset.height) < Number(asset.width) * sourceHeight);
  const direction = Number(values.direction ?? (vertical ? Direction.up : Direction.left));
  const allowed = vertical ? [Direction.up, Direction.down] : [Direction.left, Direction.right];
  if (!allowed.includes(direction)) throw new Error("Scroll direction does not match this artwork.");
  const speed = Number(values.speedPxPerSecond ?? (vertical
    ? base.requestedVerticalSpeedPxPerSecond : base.requestedHorizontalSpeedPxPerSecond));
  if (!Number.isFinite(speed) || speed < (vertical ? 5 : 1) || speed > 60)
    throw new Error(vertical ? "Vertical speed must be 5-60 pixels/second." : "Horizontal speed must be 1-60 pixels/second.");
  return { type: "scroll", ...base, direction, speedPxPerSecond: speed, loopArtwork: false };
}

export function interactionTargetContentIds(items) {
  const result = [];
  const seen = new Set();
  for (const item of Array.isArray(items) ? items : []) {
    for (const area of Array.isArray(item?.interactions?.touchAreas) ? item.interactions.touchAreas : []) {
      const kind = Number(area?.action?.kind);
      const id = String(area?.action?.targetContentId ?? "").trim();
      if (![1, 5].includes(kind) || !id || seen.has(id)) continue;
      seen.add(id);
      result.push(id);
    }
  }
  return result;
}

function capturePlaylistTargets(items) {
  const bindings = [];
  for (const item of items) {
    for (const area of Array.isArray(item?.interactions?.touchAreas) ? item.interactions.touchAreas : []) {
      const action = area?.action;
      const target = Number.isInteger(action?.targetPlaylistIndex) ? items[action.targetPlaylistIndex] : null;
      if (Number(action?.kind) === 6) bindings.push({ action, target });
    }
  }
  return bindings;
}

function restorePlaylistTargets(items, bindings) {
  for (const { action, target } of bindings) {
    const index = target ? items.indexOf(target) : -1;
    action.targetPlaylistIndex = index >= 0 ? index : null;
    action.targetPlaylistItemId = index >= 0 && items[index]?.instanceId ? items[index].instanceId : null;
  }
}

export function movePlaylistItemWithTargets(items, index, destination) {
  if (!Array.isArray(items) || index < 0 || index >= items.length || destination < 0 || destination >= items.length)
    return false;
  const bindings = capturePlaylistTargets(items);
  const [item] = items.splice(index, 1);
  items.splice(destination, 0, item);
  restorePlaylistTargets(items, bindings);
  return true;
}

export function duplicatePlaylistItemWithTargets(items, index) {
  if (!Array.isArray(items) || !Number.isInteger(index) || index < 0 || index >= items.length)
    throw new Error("Select a playlist image to duplicate.");
  if (items.length >= 16) throw new Error("A Pirdy playlist supports up to 16 items.");
  const source = items[index], copy = structuredClone(source), bindings = capturePlaylistTargets(items);
  if (source.instanceId) copy.instanceId = crypto.randomUUID();
  for (const area of copy.interactions?.touchAreas ?? []) {
    const action = area.action;
    if (Number(action?.kind) === 6) {
      const target = items[action.targetPlaylistIndex];
      bindings.push({ action, target: target === source ? copy : target });
    } else if (source.instanceId && action?.targetPlaylistItemId === source.instanceId)
      action.targetPlaylistItemId = copy.instanceId;
  }
  items.splice(index + 1, 0, copy);
  restorePlaylistTargets(items, bindings);
  return copy;
}

export function removePlaylistItemWithTargets(items, index) {
  if (!Array.isArray(items) || index < 0 || index >= items.length) return false;
  const bindings = capturePlaylistTargets(items);
  items.splice(index, 1);
  restorePlaylistTargets(items, bindings);
  return true;
}

export function clampHotspot(region, asset) {
  const width = clampInteger(region.width, 1, Number(asset.width));
  const height = clampInteger(region.height, 1, Number(asset.height));
  return {
    x: clampInteger(region.x, 0, Number(asset.width) - width),
    y: clampInteger(region.y, 0, Number(asset.height) - height),
    width,
    height,
  };
}

export function transformHotspot(region, deltaX, deltaY, asset, resizing = false) {
  const current = clampHotspot(region, asset);
  const xDelta = Math.round(Number(deltaX) || 0);
  const yDelta = Math.round(Number(deltaY) || 0);
  if (!resizing) return clampHotspot({ ...current, x: current.x + xDelta, y: current.y + yDelta }, asset);
  const maximumWidth = Number(asset.width) - current.x;
  const maximumHeight = Number(asset.height) - current.y;
  return {
    ...current,
    width: clampInteger(current.width + xDelta, Math.min(32, maximumWidth), maximumWidth),
    height: clampInteger(current.height + yDelta, Math.min(32, maximumHeight), maximumHeight),
  };
}

export function hotspotBounds(area, asset) {
  const mode = Number(area?.coordinateMode ?? 1);
  if (mode === 1) return { width: Number(asset.width), height: Number(asset.height) };
  if (mode === 2) return viewportFor(asset.orientation ?? asset.compatibleOrientation, geometryProfileIdForAsset(asset));
  throw new Error("This hotspot has an unknown coordinate mode.");
}

export function validateHotspots(areas, asset) {
  for (const area of areas) if (area.overlayImage) {
    validateOverlay(area.overlayImage);
    if (Number(area.coordinateMode) !== 2) throw new Error("Overlay images need a screen-fixed hotspot.");
  }
  if (areas.length > 32) throw new Error("Use no more than 32 touch areas on one content item.");
  for (const [index, area] of areas.entries()) {
    const bounds = hotspotBounds(area, asset);
    const region = area.region;
    if (!region || ![region.x, region.y, region.width, region.height].every(Number.isInteger) ||
        region.width < 1 || region.height < 1 || region.x < 0 || region.y < 0 ||
        region.x + region.width > bounds.width || region.y + region.height > bounds.height)
      throw new Error(`Hotspot ${index + 1} must fit completely inside its ${Number(area.coordinateMode) === 2 ? "screen" : "artwork"}.`);
    for (let other = index + 1; other < areas.length; other += 1) {
      if (Number(area.coordinateMode ?? 1) !== Number(areas[other].coordinateMode ?? 1)) continue;
      const second = areas[other].region;
      if (!second) continue;
      if (region.x < second.x + second.width && region.x + region.width > second.x && region.y < second.y + second.height && region.y + region.height > second.y)
        throw new Error(`Hotspots ${index + 1} and ${other + 1} overlap.`);
    }
  }
}

export function preparationPlan(sourceWidth, sourceHeight, presentation, orientation, profileId = DeviceProfile.uno) {
  if (sourceWidth <= 0 || sourceHeight <= 0) throw new Error("The selected image has invalid dimensions.");
  const viewport = viewportFor(orientation, profileId);
  if (presentation === "fit") {
    const scale = Math.min(viewport.width / sourceWidth, viewport.height / sourceHeight);
    const scaledWidth = Math.max(1, Math.round(sourceWidth * scale));
    const scaledHeight = Math.max(1, Math.round(sourceHeight * scale));
    return {
      outputWidth: viewport.width, outputHeight: viewport.height, scaledWidth, scaledHeight,
      destinationX: Math.floor((viewport.width - scaledWidth) / 2), destinationY: Math.floor((viewport.height - scaledHeight) / 2),
      playbackMode: PlaybackMode.static,
    };
  }
  if (presentation !== "scroll") throw new Error("Choose Fit or Scroll.");
  const comparison = sourceWidth * viewport.height - viewport.width * sourceHeight;
  if (comparison === 0) throw new Error("This image already matches the Pirdy aspect ratio. Choose Fit.");
  if (comparison > 0) {
    if (Number(orientation) === Orientation.portrait)
      throw new Error("Horizontal scrolling is not supported in portrait. Choose Fit or a landscape Pirdy.");
    const width = Math.round(sourceWidth * viewport.height / sourceHeight);
    if (width <= viewport.width || width > MAX_HORIZONTAL_WIDTH) throw new Error(`Horizontal artwork must prepare between ${viewport.width + 1} and ${MAX_HORIZONTAL_WIDTH} pixels wide.`);
    return { outputWidth: width, outputHeight: viewport.height, scaledWidth: width, scaledHeight: viewport.height, destinationX: 0, destinationY: 0, playbackMode: PlaybackMode.horizontal };
  }
  const height = Math.round(sourceHeight * viewport.width / sourceWidth);
  if (height <= viewport.height || height > MAX_VERTICAL_HEIGHT) throw new Error(`Vertical artwork must prepare between ${viewport.height + 1} and ${MAX_VERTICAL_HEIGHT} pixels high.`);
  return { outputWidth: viewport.width, outputHeight: height, scaledWidth: viewport.width, scaledHeight: height, destinationX: 0, destinationY: 0, playbackMode: PlaybackMode.vertical };
}

export function encodePimg(width, height, rgba, columnMajor) {
  const payloadLength = width * height * 2;
  const bytes = new Uint8Array(HEADER_SIZE + payloadLength);
  bytes.set([0x50, 0x49, 0x4d, 0x47]);
  const view = new DataView(bytes.buffer);
  view.setUint16(4, 1, true);
  view.setUint16(6, HEADER_SIZE, true);
  view.setUint32(8, width, true);
  view.setUint32(12, height, true);
  view.setUint16(16, 1, true);
  view.setUint16(18, columnMajor ? 1 : 0, true);
  view.setBigUint64(20, BigInt(payloadLength), true);
  for (let x = 0; x < width; x += 1) {
    for (let y = 0; y < height; y += 1) {
      const sourcePixel = y * width + x;
      const targetPixel = columnMajor ? x * height + y : sourcePixel;
      const source = sourcePixel * 4;
      const value = ((rgba[source] & 0xf8) << 8) | ((rgba[source + 1] & 0xfc) << 3) | (rgba[source + 2] >> 3);
      view.setUint16(HEADER_SIZE + targetPixel * 2, value, false);
    }
  }
  const crc32 = crc32Of(bytes.subarray(HEADER_SIZE));
  view.setUint32(28, crc32, true);
  view.setUint32(32, 0, true);
  return { bytes, crc32 };
}

function crc32Of(bytes) {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit += 1) crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function bytesToHex(value) {
  return [...new Uint8Array(value)].map(byte => byte.toString(16).padStart(2, "0")).join("");
}

function clampInteger(value, minimum, maximum) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return minimum;
  return Math.max(minimum, Math.min(maximum, Math.round(numeric)));
}
